import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, axiosClient } from '../../auth-lib';
import { Button, Modal, Input, Select, StatusPill, Skeleton, useToast } from '../../design-system';
import { REGIMES, NOTICE_TYPES, STATUSES, PRIORITIES, statusLabel } from '../../lib/notices';

const OTHER = '__other__';
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const emptyForm = {
  regime: 'income_tax',
  noticeType: '',
  noticeTypeSelect: '',
  section: '',
  clientId: '',
  gstinId: '',
  referenceNo: '',
  authority: '',
  dateIssued: '',
  dateReceived: '',
  responseDueDate: '',
  demandAmount: '',
  priority: 'medium',
  status: 'new',
  assigneeId: '',
  description: '',
};

// One modal, three modes: create / edit (form) and view (detail + actions).
function NoticeModal({ mode: initialMode, noticeId, onClose, onSaved }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(emptyForm);
  const [notice, setNotice] = useState(null);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(initialMode !== 'create');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/api/clients').then((r) => setClients(r.data)).catch(() => {});
    axiosClient.get('/users').then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!noticeId) return;
    setLoading(true);
    apiClient
      .get(`/api/notices/${noticeId}`)
      .then((r) => {
        setNotice(r.data);
        const n = r.data;
        const known = (NOTICE_TYPES[n.regime] || []).some((t) => t.code === n.noticeType);
        setForm({
          ...emptyForm,
          ...n,
          clientId: n.clientId?._id || n.clientId || '',
          assigneeId: n.assigneeId?._id || n.assigneeId || '',
          noticeTypeSelect: known ? n.noticeType : OTHER,
          demandAmount: n.demandAmount ?? '',
          dateIssued: toDateInput(n.dateIssued),
          dateReceived: toDateInput(n.dateReceived),
          responseDueDate: toDateInput(n.responseDueDate),
        });
      })
      .catch((err) => toast.danger(err.response?.data?.error || 'Failed to load notice'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId]);

  const gstins = useMemo(() => {
    const c = clients.find((x) => x._id === form.clientId);
    return c?.gstins || [];
  }, [clients, form.clientId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const typeOptions = NOTICE_TYPES[form.regime] || [];

  const save = async (e) => {
    e?.preventDefault();
    setError('');
    const noticeType = form.noticeTypeSelect === OTHER ? form.noticeType : form.noticeTypeSelect;
    if (!form.clientId || !form.regime || !noticeType) {
      setError('Client, regime and notice type are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        regime: form.regime,
        noticeType,
        section: form.section,
        clientId: form.clientId,
        gstinId: form.gstinId || null,
        referenceNo: form.referenceNo,
        authority: form.authority,
        dateIssued: form.dateIssued || null,
        dateReceived: form.dateReceived || null,
        responseDueDate: form.responseDueDate || null,
        demandAmount: form.demandAmount === '' ? null : Number(form.demandAmount),
        priority: form.priority,
        status: form.status,
        assigneeId: form.assigneeId || null,
        description: form.description,
      };
      if (mode === 'create') {
        await apiClient.post('/api/notices', payload);
        toast.success('Notice logged');
      } else {
        await apiClient.put(`/api/notices/${noticeId}`, payload);
        toast.success('Notice updated');
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save notice');
    } finally {
      setSaving(false);
    }
  };

  const spawnTask = async () => {
    try {
      const res = await apiClient.post(`/api/notices/${noticeId}/tasks`, {});
      toast.success('Response task created');
      navigate(`/org-admin/tasks/${res.data._id}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to create task');
    }
  };

  const isView = mode === 'view';
  const title = mode === 'create' ? 'Add Notice' : isView ? 'Notice Detail' : 'Edit Notice';

  const footer = isView ? (
    <>
      <Button variant="secondary" onClick={onClose}>Close</Button>
      <Button variant="secondary" onClick={() => setMode('edit')}>Edit</Button>
      {!notice?.linkedTaskId ? (
        <Button variant="primary" onClick={spawnTask}>Create response task</Button>
      ) : (
        <Button variant="primary" onClick={() => navigate(`/org-admin/tasks/${notice.linkedTaskId._id || notice.linkedTaskId}`)}>
          Open response task
        </Button>
      )}
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
      <Button variant="primary" onClick={save} loading={saving}>{mode === 'create' ? 'Log Notice' : 'Save Changes'}</Button>
    </>
  );

  return (
    <Modal isOpen onClose={onClose} title={title} footer={footer} size="lg">
      {loading ? (
        <Skeleton height={260} />
      ) : isView && notice ? (
        <div className="notice-detail">
          <div className="notice-detail-head">
            <StatusPill variant={notice.regime === 'gst' ? 'info' : 'neutral'} label={notice.regime === 'gst' ? 'GST' : 'Income Tax'} />
            <span className="notice-detail-type">{notice.noticeType}</span>
            {notice.section && <span className="notice-section">{notice.section}</span>}
            <StatusPill status={notice.status} label={statusLabel(notice.status)} />
          </div>
          <dl className="notice-detail-grid">
            <div><dt>Client</dt><dd>{notice.clientId?.displayName || '—'}</dd></div>
            <div><dt>Reference / DIN</dt><dd>{notice.referenceNo || '—'}</dd></div>
            <div><dt>Authority</dt><dd>{notice.authority || '—'}</dd></div>
            <div><dt>Owner</dt><dd>{notice.assigneeId?.name || 'Unassigned'}</dd></div>
            <div><dt>Issued</dt><dd>{notice.dateIssued ? new Date(notice.dateIssued).toLocaleDateString('en-IN') : '—'}</dd></div>
            <div><dt>Received</dt><dd>{notice.dateReceived ? new Date(notice.dateReceived).toLocaleDateString('en-IN') : '—'}</dd></div>
            <div><dt>Response due</dt><dd className="notice-strong">{notice.responseDueDate ? new Date(notice.responseDueDate).toLocaleDateString('en-IN') : '—'}</dd></div>
            {notice.demandAmount != null && <div><dt>Demand</dt><dd>₹{Number(notice.demandAmount).toLocaleString('en-IN')}</dd></div>}
          </dl>
          {notice.description && <p className="notice-desc">{notice.description}</p>}
          {notice.linkedTaskId && (
            <div className="notice-linked">Linked task: <strong>{notice.linkedTaskId.title || 'Task'}</strong></div>
          )}
        </div>
      ) : (
        <form className="notice-form" onSubmit={save}>
          {error && <div className="notice-form-error" role="alert">{error}</div>}
          <div className="notice-form-row">
            <Select label="Regime *" value={form.regime} onChange={(e) => setForm((f) => ({ ...f, regime: e.target.value, noticeTypeSelect: '', gstinId: '' }))}>
              {REGIMES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </Select>
            <Select label="Notice type *" value={form.noticeTypeSelect} onChange={set('noticeTypeSelect')}>
              <option value="">— Select —</option>
              {typeOptions.map((t) => <option key={t.code} value={t.code}>{t.code} — {t.desc}</option>)}
              <option value={OTHER}>Other…</option>
            </Select>
          </div>
          {form.noticeTypeSelect === OTHER && (
            <Input label="Notice type (custom)" value={form.noticeType} onChange={set('noticeType')} placeholder="e.g. 200A" />
          )}
          <div className="notice-form-row">
            <Select label="Client *" value={form.clientId} onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value, gstinId: '' }))}>
              <option value="">— Select client —</option>
              {clients.map((c) => <option key={c._id} value={c._id}>{c.displayName}</option>)}
            </Select>
            {form.regime === 'gst' && gstins.length > 0 && (
              <Select label="GSTIN" value={form.gstinId} onChange={set('gstinId')}>
                <option value="">— Any / not specific —</option>
                {gstins.map((g) => <option key={g._id} value={g._id}>{g.gstin}</option>)}
              </Select>
            )}
          </div>
          <div className="notice-form-row">
            <Input label="Reference / DIN" value={form.referenceNo} onChange={set('referenceNo')} placeholder="Portal ref or DIN" />
            <Input label="Authority / Officer" value={form.authority} onChange={set('authority')} />
          </div>
          <div className="notice-form-row three">
            <Input label="Date issued" type="date" value={form.dateIssued} onChange={set('dateIssued')} />
            <Input label="Date received" type="date" value={form.dateReceived} onChange={set('dateReceived')} />
            <Input label="Response due *" type="date" value={form.responseDueDate} onChange={set('responseDueDate')} />
          </div>
          <div className="notice-form-row three">
            <Input label="Demand amount (₹)" type="number" value={form.demandAmount} onChange={set('demandAmount')} />
            <Select label="Priority" value={form.priority} onChange={set('priority')}>
              {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </Select>
            <Select label="Status" value={form.status} onChange={set('status')}>
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
          </div>
          <Select label="Owner" value={form.assigneeId} onChange={set('assigneeId')}>
            <option value="">— Unassigned —</option>
            {users.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
          </Select>
          <Input label="Notes" value={form.description} onChange={set('description')} placeholder="Summary of what the notice demands" />
        </form>
      )}
    </Modal>
  );
}

export default NoticeModal;
